<template>
  <div
    ref="element"
    class="z-10 flex h-[53px] shrink-0 items-center justify-between gap-4 bg-background px-4 transition"
    :class="[isScrolled && `scrolling`]"
  >
    <div class="relative flex h-full min-w-0 items-center">
      <div
        class="shrink-0 transition-width duration-200 ease-bounce motion-reduce:transition-none"
        :class="[!isSidebarOpen ? `w-[1.875rem]` : `w-0`]"
      >
        <Transition
          enter-active-class="duration-200"
          enter-from-class="-translate-x-full"
          enter-to-class="translate-x-0"
          leave-active-class="duration-200"
          leave-from-class="translate-x-0"
          leave-to-class="-translate-x-full"
        >
          <div
            v-if="!isSidebarOpen"
            class="absolute inset-y-0 -left-2 grid place-items-center transition ease-bounce motion-reduce:transition-none"
          >
            <button
              type="button"
              aria-label="Open sidebar"
              class="grid size-[1.875rem] shrink-0 place-items-center rounded-full text-gray-700 outline-2 outline-action transition hover:bg-gray-200 focus-visible:outline active:bg-gray-200 dark:text-muted-foreground dark:hover:bg-card dark:active:bg-card"
              @click="openSidebar"
            >
              <Bars3Icon class="size-4" />
            </button>
          </div>
        </Transition>
      </div>

      <DesktopBreadcrumbs
        v-if="isHeaderWide"
        :crumbs="breadcrumbs"
        :pending="pending"
        class="-ml-2"
      />
      <MobileBreadcrumbs
        v-else
        :crumbs="breadcrumbs"
        :pending="pending"
        class="-ml-2"
      />

      <Tooltip.Hint
        v-if="starrable && !pending"
        text="Starred views appear in your home page"
        size="xs"
        class="-ml-1"
      >
        <button
          type="button"
          :aria-label="!starred ? `Star this page` : `Unstar this page`"
          class="grid size-[1.875rem] shrink-0 place-items-center rounded-full transition hover:bg-gray-300 active:bg-gray-300 dark:hover:bg-card dark:active:bg-card"
          :class="[!starred ? `text-muted-foreground` : `text-yellow-500`]"
          @click="handleStarClick"
        >
          <StarIcon class="size-4" />
        </button>
      </Tooltip.Hint>
    </div>

    <div class="-mr-2 flex min-w-0 shrink-0 items-center justify-end gap-2">
      <slot></slot>
    </div>
  </div>

  <Dialog
    v-if="showNameDialog"
    title="Name this view"
    variant="narrow"
    @close="showNameDialog = false"
  >
    <div class="mb-px flex flex-col gap-4">
      <p class="text-sm text-muted-foreground">
        Give this starred view a custom name to make it easier to identify on
        your home page. (Optional)
      </p>

      <Forms.Input
        v-model="bookmarkLabel"
        placeholder="View name (optional)"
        @keydown.enter="saveBookmark"
      />
    </div>

    <template #actions>
      <ActionButton
        text="Cancel"
        variant="secondary"
        @click="showNameDialog = false"
      />

      <ActionButton text="Save" variant="primary" @click="saveBookmark" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import isEqual from "fast-deep-equal";
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  toRef,
  useTemplateRef,
} from "vue";
import { useRoute } from "vue-router";

import ActionButton from "@/components/utilities/ActionButton.vue";
import Forms from "@/design_system/Form";
import Dialog from "@/design_system/shared/Dialog/Dialog.vue";
import Tooltip from "@/design_system/Tooltip";
import Bars3Icon from "@/icons/heroicons/bars-3-micro.svg";
import StarIcon from "@/icons/heroicons/star-micro.svg";
import { useMediaQuery } from "@/lib/composables/media-query";
import { createResizeObserver } from "@/lib/composables/resize-observer";
import { SEARCH_CONTEXT } from "@/search/routes";
import { useStore as useUiStatesStore } from "@/store/stores/ui-states";

import type { BreadcrumbPath } from "./internal/breadcrumb-types";
import DesktopBreadcrumbs from "./internal/DesktopBreadcrumbs.vue";
import { findCurrentRoute } from "./internal/lib";
import MobileBreadcrumbs from "./internal/MobileBreadcrumbs.vue";

export type {
  BreadcrumbItem,
  BreadcrumbPath,
  RouteBreadcrumbItem,
} from "./internal/breadcrumb-types";

export interface Props {
  crumbs: BreadcrumbPath;
  pending?: boolean;
}

const uiStates = useUiStatesStore();

const element = useTemplateRef("element");
const isScrolled = ref(false);
const showNameDialog = ref(false);
const bookmarkLabel = ref("");

const props = defineProps<Props>();

const route = useRoute();
const isWindowWide = useMediaQuery("(width >= 1024px)");
const isHeaderWide = shallowRef<boolean>();

const isDesktopSidebarOpen = toRef(uiStates, "isDesktopSidebarOpen");
const isMobileSidebarOpen = toRef(uiStates, "isMobileSidebarOpen");

const isSidebarOpen = computed(() => {
  return isWindowWide.value ? isDesktopSidebarOpen.value : false;
});

createResizeObserver(element, (entry) => {
  isHeaderWide.value = entry.contentRect.width > 480;
});

const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement;
  isScrolled.value = target.scrollTop > 0;
};

onMounted(() => {
  const container = document.querySelector('[data-target="page-content"]');
  if (container) {
    container.addEventListener("scroll", handleScroll, { capture: true });

    onUnmounted(() => {
      container.removeEventListener("scroll", handleScroll, { capture: true });
    });
  }
});

const openSidebar = () => {
  if (isWindowWide.value) {
    isDesktopSidebarOpen.value = true;
  } else {
    isMobileSidebarOpen.value = true;
  }
};

const matchingBookmark = computed(() => {
  if (!route.name || typeof route.name !== "string") {
    return null;
  }

  return uiStates.bookmarks
    .filter((item) => item.name in SEARCH_CONTEXT)
    .find((item) => {
      // For backward compatibility with old bookmarks that don't have path
      // Only match if both the bookmark and current route have no query params
      if (!item.path && item.name === route.name) {
        return Object.keys(route.query).length === 0;
      }

      return matchesBookmark(item);
    });
});

const breadcrumbs = computed((prev?: BreadcrumbPath): BreadcrumbPath => {
  const next = props.crumbs;
  const baseCrumbs = prev !== undefined && isEqual(prev, next) ? prev : next;

  // If there's a matching bookmark with a label, replace the last breadcrumb title
  const bookmark = matchingBookmark.value;
  if (bookmark?.label && baseCrumbs.length > 0) {
    const lastIndex = baseCrumbs.length - 1;
    const lastCrumb = baseCrumbs[lastIndex];
    if (lastCrumb) {
      return [
        ...baseCrumbs.slice(0, lastIndex),
        {
          ...lastCrumb,
          title: bookmark.label,
        },
      ] as BreadcrumbPath;
    }
  }

  return baseCrumbs;
});

const currentRoute = computed(() => findCurrentRoute());

const starrable = computed((): boolean => {
  if (!currentRoute.value) {
    return false;
  }

  return (
    SEARCH_CONTEXT[currentRoute.value.name as keyof typeof SEARCH_CONTEXT] &&
    "route" in
      SEARCH_CONTEXT[currentRoute.value.name as keyof typeof SEARCH_CONTEXT] &&
    currentRoute.value.name !== "/home"
  );
});

const matchesBookmark = (bookmark: (typeof uiStates.bookmarks)[0]): boolean => {
  const bookmarkPath = bookmark.path || bookmark.name;
  const [bookmarkPathOnly, bookmarkQueryString] = bookmarkPath.split("?");
  const currentPath = route.path;

  // Check if path matches exactly
  if (currentPath !== bookmarkPathOnly) {
    return false;
  }

  // Parse bookmark query params
  const bookmarkParams = bookmarkQueryString
    ? new URLSearchParams(bookmarkQueryString)
    : new URLSearchParams();

  // Parse current route query params
  const currentQueryString = route.fullPath.split("?")[1] || "";
  const currentParams = new URLSearchParams(currentQueryString);

  // Compare query parameters
  if (
    JSON.stringify(Object.fromEntries(bookmarkParams.entries())) !==
    JSON.stringify(Object.fromEntries(currentParams.entries()))
  ) {
    return false;
  }

  return true;
};

const starred = computed(() => !!matchingBookmark.value);

const handleStarClick = () => {
  if (starred.value) {
    // Unstar - use the same matching logic as starred check
    uiStates.bookmarks = uiStates.bookmarks.filter((item) => {
      // For backward compatibility with old bookmarks that don't have path
      if (!item.path && route.name && item.name === route.name) {
        return false;
      }

      return !matchesBookmark(item);
    });
  } else {
    // Star - only show dialog if there are query parameters
    const hasQueryParams = Object.keys(route.query).length > 0;
    if (hasQueryParams) {
      bookmarkLabel.value = "";
      showNameDialog.value = true;
    } else {
      // No query params, save directly without prompting
      saveBookmark();
    }
  }
};

const saveBookmark = () => {
  if (!route.name || typeof route.name !== "string") {
    return;
  }

  const label = bookmarkLabel.value.trim();
  const currentBookmarks = [...uiStates.bookmarks];
  currentBookmarks.push({
    name: route.name,
    path: route.fullPath,
    label: label || undefined,
  });
  uiStates.bookmarks = currentBookmarks;
  showNameDialog.value = false;
};
</script>

<style>
.scrolling {
  box-shadow: 0 1px 0 0 var(--color-muted);
}
</style>
